import { useCallback, useEffect, useState } from 'react';
import {
  activarAnalista,
  fijarPermisosAnalista,
  darAcceso,
  listarAnalistas,
  PERMISOS,
  type Analista,
  type PermisoPlataforma,
} from '../../lib/api-panel';
import {
  Boton,
  BotonEnlace,
  Campo,
  CampoCelular,
  CampoLista,
  EncabezadoTabla,
  Esqueleto,
  FilaAcciones,
  FilaCampos,
  FilaTabla,
  Insignia,
  MensajeError,
  MensajeExito,
  TablaAncha,
  Tarjeta,
  ThAcciones,
} from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { instalacion } from '@tanteo/compartido/instalacion';

export function Analistas({ puedeEscribir, esAdmin }: { puedeEscribir: boolean; esAdmin: boolean }) {
  const [analistas, setAnalistas] = useState<Analista[] | null>(null);
  const [editando, setEditando] = useState<Analista | null>(null);
  const [seleccion, setSeleccion] = useState<PermisoPlataforma[]>([]);
  const [invitando, setInvitando] = useState(false);
  const [correo, setCorreo] = useState('');
  const [rol, setRol] = useState<'analista' | 'admin'>('analista');
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [celular, setCelular] = useState('');
  const [cargo, setCargo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const cargar = useCallback(() => {
    listarAnalistas()
      .then(setAnalistas)
      .catch((e) => {
        setAnalistas([]);
        setError(e instanceof Error ? e.message : 'No pudimos cargar el equipo.');
      });
  }, []);
  useEffect(cargar, [cargar]);

  function alternar(p: PermisoPlataforma) {
    setSeleccion((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  }

  async function ejecutar(accion: () => Promise<void>) {
    setOcupado(true);
    setError('');
    setAviso('');
    try {
      await accion();
      setEditando(null);
      setInvitando(false);
      cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo completar la acción.');
    } finally {
      setOcupado(false);
    }
  }

  if (analistas === null) return <Esqueleto lineas={5} />;

  return (
    <Tarjeta>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="text-muted max-w-2xl text-contenido">
          Quién puede entrar a este panel y hasta dónde. Un administrador lo puede todo; a un analista se le otorga
          permiso por permiso. Toda alta, cambio y desactivación queda en la auditoría.
        </p>
        {puedeEscribir && (
          <Boton
            onClick={() => {
              setInvitando(true);
              setCorreo('');
              setRol('analista');
              setNombre('');
              setApellidos('');
              setCelular('');
              setCargo('');
              setSeleccion([]);
              setError('');
              setAviso('');
            }}
            data-e2e="invitar-analista"
          >
            ＋ Dar acceso al equipo de {instalacion.nombre}
          </Boton>
        )}
      </div>

      {error && !invitando && !editando && <MensajeError>{error}</MensajeError>}
      <MensajeExito e2e="analista-aviso">{aviso}</MensajeExito>

      <TablaAncha>
          <EncabezadoTabla>
              <th className="py-2 pr-2">Persona</th>
              <th className="py-2 pr-2">Rol</th>
              <th className="py-2 pr-2">Permisos</th>
              <th className="py-2 pr-2">Estado</th>
              {/* Sin permiso de escritura nadie puede actuar: rotular la columna y dejarla vacía
                  es peor que no tenerla. */}
              {puedeEscribir && <ThAcciones className="w-44" />}
          </EncabezadoTabla>
          <tbody>
            {analistas.map((a) => (
              <FilaTabla key={a.usuarioId} data-e2e="fila-analista">
                <td className="py-2 pr-2">
                  <span className="font-bold">{a.nombre ?? a.correo}</span>
                  {a.nombre && <span className="text-muted block text-etiqueta">{a.correo}</span>}
                </td>
                <td className="py-2 pr-2">
                  {a.rol === 'admin' ? <Insignia tono="info">Administrador</Insignia> : <Insignia tono="neutro">Analista</Insignia>}
                </td>
                <td className="py-2 pr-2 text-contenido">
                  {a.rol === 'admin' ? 'Todos' : a.permisos.length === 0 ? 'Ninguno' : `${a.permisos.length} de ${PERMISOS.length}`}
                </td>
                <td className="py-2 pr-2">
                  {a.activo ? <Insignia tono="exito">Activo</Insignia> : <Insignia tono="neutro">Inactivo</Insignia>}
                </td>
                {puedeEscribir && (
                  <td className="py-2 text-right">
                    {a.rol === 'admin' ? (
                      // Un administrador lo puede todo: no hay permisos que ajustarle ni sentido en
                      // desactivarlo desde aquí, y la celda lo dice.
                      <span className="text-muted text-etiqueta">sin acciones</span>
                    ) : (
                      <span className="flex justify-end gap-3">
                        <BotonEnlace
                          onClick={() => {
                            setEditando(a);
                            setSeleccion(a.permisos);
                            setError('');
                          }}
                        >
                          Permisos
                        </BotonEnlace>
                        <BotonEnlace
                          tono={a.activo ? 'peligro' : 'info'}
                          onClick={() => void ejecutar(() => activarAnalista(a.usuarioId, !a.activo))}
                        >
                          {a.activo ? 'Desactivar' : 'Activar'}
                        </BotonEnlace>
                      </span>
                    )}
                  </td>
                )}
              </FilaTabla>
            ))}
          </tbody>
        </TablaAncha>

      <Modal abierto={invitando} titulo={`Dar acceso al equipo de ${instalacion.nombre}`} onCerrar={() => setInvitando(false)}>
        <div className="space-y-3">
          <p className="text-muted text-contenido">
            Le llega un código a ese correo para entrar al Panel; no necesita tener un negocio.
          </p>
          <FilaCampos>
            <Campo etiqueta="Correo" type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} data-e2e="analista-correo" />
            <CampoLista etiqueta="Rol" value={rol} onChange={(e) => setRol(e.target.value as 'analista' | 'admin')} data-e2e="analista-rol">
              <option value="analista">Analista</option>
              {esAdmin && <option value="admin">Administrador</option>}
            </CampoLista>
          </FilaCampos>
          <FilaCampos>
            <Campo etiqueta="Nombres" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="given-name" />
            <Campo etiqueta="Apellidos" value={apellidos} onChange={(e) => setApellidos(e.target.value)} autoComplete="family-name" />
          </FilaCampos>
          <FilaCampos>
            <CampoCelular value={celular} onChange={(e) => setCelular(e.target.value)} />
            <Campo etiqueta="Cargo" value={cargo} onChange={(e) => setCargo(e.target.value)} />
          </FilaCampos>
          {rol === 'analista' ? (
            <fieldset className="grid gap-1 sm:grid-cols-2">
              <legend className="text-muted mb-1 text-contenido font-semibold">Permisos</legend>
              {PERMISOS.map((p) => (
                <label key={p.valor} className="flex items-center gap-2 text-contenido">
                  <input type="checkbox" checked={seleccion.includes(p.valor)} onChange={() => alternar(p.valor)} />
                  {p.etiqueta}
                </label>
              ))}
            </fieldset>
          ) : (
            <p className="text-muted text-contenido">Un administrador tiene todos los permisos.</p>
          )}
          <MensajeError>{error}</MensajeError>
          <FilaAcciones>
            <Boton variante="contorno" onClick={() => setInvitando(false)} disabled={ocupado}>
              Volver
            </Boton>
            <Boton
              onClick={() =>
                void ejecutar(async () => {
                  const { codigoEnviado } = await darAcceso({
                    correo,
                    rol,
                    nombres: nombre,
                    apellidos,
                    celular,
                    cargo,
                    permisos: seleccion,
                  });
                  setAviso(
                    codigoEnviado
                      ? `Listo: le enviamos a ${correo.trim()} un código para entrar.`
                      : `Listo, pero el código no salió: ${correo.trim()} puede pedirlo con «Iniciar sesión».`
                  );
                })
              }
              disabled={ocupado || !correo.includes('@') || nombre.trim().length < 2}
              data-e2e="analista-guardar"
            >
              {ocupado ? 'Guardando…' : 'Dar acceso'}
            </Boton>
          </FilaAcciones>
        </div>
      </Modal>

      <Modal abierto={editando !== null} titulo={`Permisos de ${editando?.nombre ?? editando?.correo ?? ''}`} onCerrar={() => setEditando(null)}>
        <div className="space-y-3">
          <fieldset className="grid gap-1 sm:grid-cols-2">
            {PERMISOS.map((p) => (
              <label key={p.valor} className="flex items-center gap-2 text-contenido">
                <input type="checkbox" checked={seleccion.includes(p.valor)} onChange={() => alternar(p.valor)} />
                {p.etiqueta}
              </label>
            ))}
          </fieldset>
          <p className="text-muted text-etiqueta">Lo que quede sin marcar se revoca al guardar.</p>
          <MensajeError>{error}</MensajeError>
          <FilaAcciones>
            <Boton variante="contorno" onClick={() => setEditando(null)} disabled={ocupado}>
              Volver
            </Boton>
            <Boton
              onClick={() => editando && void ejecutar(() => fijarPermisosAnalista(editando.usuarioId, seleccion))}
              disabled={ocupado}
            >
              {ocupado ? 'Guardando…' : 'Guardar permisos'}
            </Boton>
          </FilaAcciones>
        </div>
      </Modal>
    </Tarjeta>
  );
}
